import React, { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import "./Layout.scss";
import { Footer } from "../Footer/Footer";
import { useRouteChecks } from "../../hooks/useRouteChecks";
import { Header } from "../Header/Header";
import { ScrollToTop } from "../__common/ScrollToTop/ScrollToTop";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { EuCookieAlert, NonEuCookieAlert } from "../CookieModal/CookieModal";
import { UserState } from "../../store/reducers/user-reducer";
import { HelpTipsPoupup } from "../HelpTipsPopup/HelpTipsPopup";
import { ROUTES } from "../../configs/routes";
import classNames from "classnames";
import { useLoginOptionHooks } from "../../hooks/login-option-hooks";
import { LoginOptionState } from "../../store/reducers/login-option-reducer";
import { UtilsUser } from "../../utils/UtilsUser";
import { IdleTimeoutDialog } from "../__common/dialog/IdleTimeoutDialog/idleTimeoutDialog";
import { useEmailLoginHooks } from "../../hooks/email-login-hooks";
import { useLayoutStartup } from "../../hooks/layout-startup-hooks";
import { useAuthRouteLoginHandler } from "../../hooks/auth-route-login-hooks";
import { AuthModals } from "./AuthModals";
import { SHOW_TIMEOUT_DIALOG } from "../../constants/storage";
import { MaintenancePage } from "../MaintenancePage/MaintenancePage";
interface Props {
  children: React.ReactNode;
}

export const Layout: React.FC<Props> = (props) => {
  // third party hooks
  const { pathname } = useLocation();
  // custom hooks
  const { toggleLoginModal, toggleEmailLoginModal, toggleEmailOtpModal } =
    useLoginOptionHooks();

  const { shouldShowCookieNotice, shouldShowHeaderFooter, shouldShowHelpTips } =
    useRouteChecks(pathname);

  // redux states
  const { isLoginModalOpen, isEmailLoginModalOpen, isEmailOtpModalOpen } =
    useSelector<ApplicationState, LoginOptionState>(
      (state) => state.loginOption
    );
  const { isGeoIPLoading } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const handleLoginModalClose = useCallback(
    () => toggleLoginModal({ isLoginModalOpen: false }),
    [toggleLoginModal]
  );
  // props for auth modals component
  const authModalsProps = useMemo(
    () => ({
      isLoginModalOpen: !UtilsUser.isUserLoggedIn() && isLoginModalOpen,
      isEmailLoginModalOpen: isEmailLoginModalOpen ?? false,
      isEmailOtpModalOpen: isEmailOtpModalOpen ?? false,
      onLoginModalClose: handleLoginModalClose,
      onEmailLoginModalClose: toggleEmailLoginModal,
      onOtpModalClose: toggleEmailOtpModal,
    }),
    [
      isLoginModalOpen,
      isEmailLoginModalOpen,
      isEmailOtpModalOpen,
      toggleEmailLoginModal,
      toggleEmailOtpModal,
    ]
  );

  // variables
  const isHomePage = pathname === ROUTES.ROOT;
  const timeOutDialog = localStorage.getItem(SHOW_TIMEOUT_DIALOG);
  const isTimeOutDialogOpen = timeOutDialog === "true";

  // THIS WILL ALSO INVOKE OTHER LAYOUT RENDER CONDITION AND EFFECTS LIKE REDIRECTION SCENARIOS AND ETC.
  const { hasPageAccess, maintenanceState } = useLayoutStartup();
  // FOR EMAIL LOGIN SCENARIOS
  useEmailLoginHooks();
  // FOR HANDLING DEEPLINK OF AUTH ROUTES PAGES AND DISPLAYING EITHER LOGIN MODAL OR NORMAL SA LOGIN
  useAuthRouteLoginHandler();
  // Early return if i18next is not initialized (e.g., CN users redirected to local site)
  // This prevents rendering untranslated content
  if (!hasPageAccess) {
    return <div data-testid="No-access"></div>;
  }

  // RETURN MAINTENANCE PAGE IF MAINTENANCE IS ENABLED
  if (maintenanceState.enabled) {
    return (
      <div className="body" data-testid="layout">
        <Header isMaintenanceMode={true} />
        <main
          id="main-body"
          className={classNames({
            "snap-scroll": isHomePage,
          })}
        >
          <div className="layout" id="layout-container">
            <MaintenancePage maintenanceState={maintenanceState} />
          </div>
        </main>
      </div>
    );
  }

  // MAIN LAYOUT
  return (
    <div className="body" data-testid="layout">
      {/* Dialog Modal */}
      <IdleTimeoutDialog isTimeOutDialogOpen={isTimeOutDialogOpen} />
      {/* Auth related modal container */}
      <AuthModals {...authModalsProps} />
      {/* Cookie Notice */}
      {!isGeoIPLoading && shouldShowCookieNotice && (
        <>
          <NonEuCookieAlert />
          <EuCookieAlert />
        </>
      )}
      {/* Main Body */}
      {shouldShowHeaderFooter && <Header isMaintenanceMode={false} />}
      <main
        id="main-body"
        className={classNames({
          "snap-scroll": isHomePage,
        })}
      >
        {/* TO DO: use reactPortal for rendering page popup to centralize its positioning */}
        {shouldShowHelpTips && <HelpTipsPoupup />}
        <div className="layout" id="layout-container">
          {props.children}
        </div>
        <ScrollToTop />
        {shouldShowHeaderFooter && <Footer />}
      </main>
    </div>
  );
};
